import { useLayoutEffect, useRef, useState } from "react";

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const isWhite = (hex) => !hex || hex.toUpperCase() === "#FFFFFF";

export default function MockupPreview({
  image,
  colorHex,
  colorName,
  logoUrl,
  logoPlacement,
  onLogoPlacement,
  text,
  textPlacement,
  textColor = "#111111",
  onTextPlacement,
  className = "",
}) {
  const ref = useRef(null);
  const active = useRef(null);
  const [w, setW] = useState(0);

  useLayoutEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setW(e.contentRect.width));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  const lp = logoPlacement || { x: 50, y: 42, scale: 26 };
  const tp = textPlacement || { x: 50, y: 74, size: 7 };
  const interactive = !!(onLogoPlacement || onTextPlacement);

  const startDrag = (onChange, placement) => (e) => {
    if (!onChange) return;
    e.stopPropagation();
    active.current = { onChange, placement };
    ref.current.setPointerCapture(e.pointerId);
  };
  const onMove = (e) => {
    if (!active.current || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = clamp(((e.clientX - r.left) / r.width) * 100, 6, 94);
    const y = clamp(((e.clientY - r.top) / r.height) * 100, 6, 94);
    active.current.onChange({ ...active.current.placement, x, y });
  };
  const onUp = () => {
    active.current = null;
  };

  const maskStyle = {
    background: colorHex,
    mixBlendMode: "multiply",
    WebkitMaskImage: `url("${image}")`,
    maskImage: `url("${image}")`,
    WebkitMaskSize: "contain",
    maskSize: "contain",
    WebkitMaskRepeat: "no-repeat",
    maskRepeat: "no-repeat",
    WebkitMaskPosition: "center",
    maskPosition: "center",
  };

  return (
    <div
      ref={ref}
      onPointerMove={onMove}
      onPointerUp={onUp}
      onPointerLeave={onUp}
      className={`relative overflow-hidden select-none bg-white ${className}`}
    >
      <img src={image} alt="" draggable={false} className="w-full h-full object-contain" />
      {!isWhite(colorHex) && <div className="absolute inset-0 pointer-events-none" style={maskStyle} />}

      {colorName && (
        <span className="absolute top-3 right-3 z-20 flex items-center gap-2 bg-white/90 backdrop-blur px-2.5 py-1 font-mono text-[10px] uppercase tracking-widest text-black shadow-sm pointer-events-none">
          <span className="w-3 h-3 rounded-full border border-black/10" style={{ background: colorHex }} />
          {colorName}
        </span>
      )}

      {logoUrl && (
        <img
          src={logoUrl}
          alt="logo"
          draggable={false}
          onPointerDown={startDrag(onLogoPlacement, lp)}
          className={onLogoPlacement ? "cursor-grab active:cursor-grabbing z-10" : "z-10"}
          style={{
            position: "absolute",
            left: `${lp.x}%`,
            top: `${lp.y}%`,
            width: `${lp.scale}%`,
            transform: "translate(-50%, -50%)",
            objectFit: "contain",
            touchAction: "none",
            filter: "drop-shadow(0 3px 8px rgba(0,0,0,0.22))",
          }}
        />
      )}

      {text && (
        <div
          onPointerDown={startDrag(onTextPlacement, tp)}
          className={onTextPlacement ? "cursor-grab active:cursor-grabbing z-10" : "z-10"}
          style={{
            position: "absolute",
            left: `${tp.x}%`,
            top: `${tp.y}%`,
            transform: "translate(-50%, -50%)",
            color: textColor,
            fontWeight: 800,
            fontFamily: "'Cabinet Grotesk', sans-serif",
            fontSize: w ? `${(w * tp.size) / 100}px` : `${tp.size}%`,
            whiteSpace: "nowrap",
            touchAction: "none",
            letterSpacing: "-0.01em",
            textShadow: "0 1px 3px rgba(0,0,0,0.18)",
          }}
        >
          {text}
        </div>
      )}

      {interactive && (logoUrl || text) && (
        <span className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 font-mono text-[10px] uppercase tracking-widest bg-black/70 text-white px-2 py-1 pointer-events-none">
          Glissez pour placer
        </span>
      )}
    </div>
  );
}
